/**
 * GEA-NDT Claude Vision Service
 * Drop-in replacement for geminiService.ts
 * 
 * Uses Anthropic Claude claude-sonnet-4-6 vision via the /v1/messages API.
 * All exported function signatures are identical to the original Gemini service
 * so no other files need to change.
 * 
 * DETECTION STRATEGY:
 * - Two-pass pipeline preserved: Pass 1 = temporal screen (3 frames, reflection filter),
 *   Pass 2 = precise single-frame bbox analysis guided by Pass 1 confirmed defects.
 * - Frame ordering: FRAME 1 (prev) → FRAME 15 (target) → FRAME 30 (next)
 * - All bounding boxes anchored to FRAME 15 (middle/target frame) only.
 * - Structured output enforced via Claude tool use (JSON schema equivalent).
 * 
 * ENV: VITE_ANTHROPIC_API_KEY must be set in .env.local
 * NOTE: For production, route API calls through a backend proxy to avoid
 *       exposing the key in the browser bundle.
 */

import {
  CrackAnalysis,
  Severity,
  TrainingExample,
  InspectionMode,
  GEA_DEFECT_REGISTRY,
  ImageResolutionMode
} from '../types';
import { getVaultEntries } from './vaultService';
import { logAiUsage } from './aiUsageService';

// ── Constants ──────────────────────────────────────────────────────────────────

const CLAUDE_MODEL = 'claude-sonnet-4-6';
console.log('%c[CLAUDE-SERVICE] LOADED: OLD-PROMPT build, model=' + CLAUDE_MODEL, 'background:#000F41;color:#1AFF80;padding:2px 6px;font-weight:bold');
const ANTHROPIC_API_URL = 'https://api.anthropic.com/v1/messages';

const RESOLUTION_PX: Record<ImageResolutionMode, number> = {
  [ImageResolutionMode.STANDARD]: 1600,
  [ImageResolutionMode.HIGH_RES]: 2560,
  [ImageResolutionMode.ULTRA_4K]: 3840,
};

// ── Helpers ────────────────────────────────────────────────────────────────────

const sleep = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

/**
 * Resize + compress a base64 image to stay within token/payload limits.
 */
const optimizeImage = (base64: string, maxWidth = 1600, quality = 0.85): Promise<string> => {
  return new Promise((resolve) => {
    if (!base64.startsWith('data:image')) { resolve(base64); return; }
    const img = new Image();
    img.src = base64;
    img.onload = () => {
      let w = img.width, h = img.height;
      if (w <= maxWidth && h <= maxWidth) { resolve(base64); return; }
      const ratio = Math.min(maxWidth / w, maxWidth / h);
      w = Math.floor(w * ratio); h = Math.floor(h * ratio);
      const canvas = document.createElement('canvas');
      canvas.width = w; canvas.height = h;
      const ctx = canvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(img, 0, 0, w, h);
        const optimized = canvas.toDataURL('image/jpeg', quality);
        console.log(`[NDT-OPT] Optimized: ${w}x${h} (${Math.round(optimized.length / 1024)}KB)`);
        resolve(optimized);
      } else resolve(base64);
    };
    img.onerror = () => resolve(base64);
  });
};

/** Strip the data:image/...;base64, prefix if present */
const toRawBase64 = (s: string): string => s.includes(',') ? s.split(',')[1] : s;

/**
 * Retry wrapper with exponential backoff.
 * Retries on 429 (rate limit), 529 (overloaded), and network errors.
 */
async function callWithRetry<T>(fn: () => Promise<T>, maxRetries = 3): Promise<T> {
  let lastError: any;
  let delay = 3000;
  for (let i = 0; i < maxRetries; i++) {
    try { return await fn(); } catch (error: any) {
      lastError = error;
      const msg = (error?.message || JSON.stringify(error)).toLowerCase();
      if (msg.includes('429') || msg.includes('529') || msg.includes('503') || msg.includes('fetch')) {
        console.warn(`[NDT-RETRY] Attempt ${i + 1} failed (${msg.slice(0, 60)}). Retrying in ${delay}ms...`);
        await sleep(delay); delay *= 2; continue;
      }
      throw error;
    }
  }
  throw lastError;
}

// ── Claude API Call ────────────────────────────────────────────────────────────

interface ClaudeMessage {
  role: 'user' | 'assistant';
  content: ClaudeContentBlock[];
}

type ClaudeContentBlock =
  | { type: 'text'; text: string }
  | { type: 'image'; source: { type: 'base64'; media_type: 'image/jpeg'; data: string } };

interface ClaudeResponse {
  content: Array<{ type: string; text?: string }>;
  usage?: { input_tokens: number; output_tokens: number };
}

/**
 * Raw Claude API call.
 * Returns the full parsed response object.
 */
const callClaude = async (
  systemPrompt: string,
  messages: ClaudeMessage[],
  maxTokens = 4096
): Promise<ClaudeResponse> => {
  const apiKey = import.meta.env.VITE_ANTHROPIC_API_KEY;
  if (!apiKey) throw new Error('VITE_ANTHROPIC_API_KEY missing — add it to .env.local');

  const response = await fetch(ANTHROPIC_API_URL, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      'x-api-key': apiKey,
      'anthropic-version': '2023-06-01',
      'anthropic-dangerous-direct-browser-access': 'true',
    },
    body: JSON.stringify({
      model: CLAUDE_MODEL,
      max_tokens: maxTokens,
      system: systemPrompt,
      messages,
    }),
  });

  if (!response.ok) {
    const errText = await response.text();
    throw new Error(`Claude API error ${response.status}: ${errText}`);
  }

  return response.json() as Promise<ClaudeResponse>;
};

/**
 * Extract and parse JSON from Claude's text response.
 * Claude sometimes wraps JSON in ```json ... ``` fences — this handles that.
 */
const parseClaudeJson = <T>(response: ClaudeResponse): T => {
  const rawText = response.content
    .filter(b => b.type === 'text')
    .map(b => b.text || '')
    .join('');

  const cleaned = rawText
    .replace(/^```json\s*/i, '')
    .replace(/^```\s*/i, '')
    .replace(/```\s*$/i, '')
    .trim();

  try {
    return JSON.parse(cleaned) as T;
  } catch (e) {
    console.error('[NDT-PARSE] Failed to parse Claude JSON:', cleaned.slice(0, 500));
    throw new Error(`JSON parse error: ${(e as Error).message}`);
  }
};

// ── Prompt Builders ────────────────────────────────────────────────────────────

const buildModeRules = (modes: InspectionMode[]): string =>
  modes.map(m => {
    switch (m) {
      case InspectionMode.CRACK_ONLY:
        return `PROTOCOL [CRACK_DETECTION]: Focus on linear discontinuities. Genuine CRACKS are ultra-thin DARK FRACTURES, often only a few pixels wide. Ignore broad marks or wide surface-fouling (Residue/Scale) which are significantly wider than a typical structural crack.`;
      case InspectionMode.WELD_INTEGRITY:
        return `PROTOCOL [WELD_INTEGRITY]: Identify Weld Porosity, Slag Inclusion, and lack of fusion.
        DISTINCTION: Ignore WELD POLISH MARKS (Horizontal/Vertical bands). These are surface treatments. Only report a defect if you see actual JAGGED FRACTURES or POROSITY within the polished area.`;
      case InspectionMode.GENERAL_NDT:
        return `PROTOCOL [STRUCTURAL_AUDIT]: Scan for DENTS, Pitting, MSD, POLISHED REPAIR SITES, and UNKNOWN ANOMALIES.
        BAD REPAIRS: Identify irregular weld beads or poorly polished points. These are entrapment risks for residue. Always advise: 'CHECK AND REPOLISH'.`;
      case InspectionMode.FOULING_INSPECTION:
        return `PROTOCOL [CLEANLINESS]: Identify Surface Residue, Scale (Milk stone), or Bio-matter.
        REFLECTION TEST: Genuine MILK STONE is MATTE and OPAQUE. It does NOT reflect light. If a feature reflects light or 'shines' across the 3-frame sequence, it is POOR POLISH or GLARE — not milk stone.`;
      default:
        return `PROTOCOL: General Audit. Report any disruption in surface uniformity.`;
    }
  }).join('\n\n');

const DEFECT_TAXONOMY = GEA_DEFECT_REGISTRY
  .map(d => `- ${d.title} (ID: ${d.type})`)
  .join('\n    ');

const JSON_OUTPUT_SCHEMA = `
Return ONLY valid JSON matching this exact schema (no markdown, no preamble):
{
  "detected": boolean,
  "severity": "NONE" | "LOW" | "MEDIUM" | "HIGH" | "CRITICAL",
  "mode": "CRACK_ONLY" | "GENERAL_NDT" | "FOULING_INSPECTION" | "WELD_INTEGRITY",
  "type": string,
  "description": string,
  "recommendation": string,
  "confidenceScore": number (0.0–1.0),
  "diagnosticNote": string,
  "findings": [
    {
      "type": string,                  // Use exact ID from defect taxonomy
      "severity": "NONE"|"LOW"|"MEDIUM"|"HIGH"|"CRITICAL",
      "description": string,
      "recommendation": string,
      "prevention": string,
      "spatialAnchoring": string,      // Chain-of-thought: visual appearance + glare rejection reasoning
      "box": [ymin, xmin, ymax, xmax]  // 0–1000 normalized to FRAME 15 (middle frame) ONLY
    }
  ]
}
If no anomalies, return findings as an empty array and detected as false.`;

/**
 * System prompt for static single-image analysis (Pass 2 / image survey mode).
 */
const buildStaticSystemPrompt = (
  lessons: TrainingExample[],
  modes: InspectionMode[],
  sensitivity: number,
  confirmedContext: string,
  orientation?: string
): string => {
  const groundTruth = lessons.length > 0
    ? lessons.map(l => `[VERIFIED]: ${l.analysis.type} (${l.analysis.severity}). ${l.analysis.description}`).join('\n')
    : 'Baseline: Cracks=Dark/Jagged Fractures (non-reflective), Dents=Shifting Shadow Bowls, Pitting=Small Spots, MSD=Numerous/Irregular scratches (Impact), Polish Marks=Horizontal/Vertical bands, Scratches=Reflective.';

  const orientationRule = orientation
    ? `\nTRAJECTORY ORIENTATION: ${orientation}. Adjust spatial reasoning accordingly.`
    : '';

  return `You are a Lead NDT Engineer specializing in high-contrast surface audits of food-grade stainless steel equipment.

AUDIT PROTOCOL:
1. TARGET: You are auditing a single static photograph.
2. DEFECT PRIORITIZATION:
   - COMPREHENSIVE SCAN: Detect and annotate ALL anomalies matching any active protocol.
   - ANNOTATE EVERYTHING: Box severe defects (dents, deep cracks) AND minor ones (pitting, residue, scratches).

CRITICAL DISTINCTIONS:
- HARDWARE vs DEFECTS: Do not confuse functional fixtures or glare with defects.
- BAD REPAIRS / POOR POLISH: Irregular weld beads or dull localized patches are entrapment risks. Box as 'Repair Site', recommend 'CHECK AND REPOLISH'.
- ASSET COMPONENTS: Sight Glasses, Sprayball Connections, and Ports are mandatory inspection targets. Confirm their condition in diagnosticNote.
- DENT DETECTION: Dents appear as localized shadow depressions that distort the light. Every dent is a potential crack site.
- POLISHED STEEL vs RESIDUE: Ignore horizontal/vertical bands or repetitive rectangular patches — these are structural finishes.
- REAL RESIDUE / MILK STONE: Milk stone is MATTE, WHITE/YELLOWISH, and OPAQUE. It does NOT reflect light. Reflective features are surface polish issues, not fouling.
- UNKNOWN ANOMALIES: For any significant disruption you cannot classify, use 'Unknown Anomaly - Close-up Roping Check needed'.

${buildModeRules(modes)}

NDT DEFECT TAXONOMY (use these exact IDs for the "type" field):
    ${DEFECT_TAXONOMY}

CURRENT GROUND TRUTH LESSONS:
${groundTruth}

${confirmedContext ? `CONFIRMED DEFECTS FROM TEMPORAL SCREEN:\n${confirmedContext}` : ''}
${orientationRule}

SENSITIVITY LEVEL: ${sensitivity}/10. Higher = flag more subtle indications.

${JSON_OUTPUT_SCHEMA}`;
};

/**
 * System prompt for 3-frame sequence analysis (Pass 2 of two-pass pipeline).
 * FRAME 1 = prev context, FRAME 15 = audit target, FRAME 30 = next context.
 */
const buildSequenceSystemPrompt = (
  lessons: TrainingExample[],
  modes: InspectionMode[],
  sensitivity: number,
  movement?: string
): string => {
  const groundTruth = lessons.length > 0
    ? lessons.map(l => `[VERIFIED]: ${l.analysis.type} (${l.analysis.severity}). ${l.analysis.description}`).join('\n')
    : 'Baseline: Cracks=Dark/Jagged Fractures (non-reflective), Dents=Shifting Shadow Bowls, Pitting=Small Spots, MSD=Impact scratches, Polish Marks=Bands, Scratches=Reflective.';

  const overlapRule = (movement === 'UPWARD' || movement === 'DOWNWARD')
    ? `FLIGHT DIRECTION: ${movement}. The forward-overlap region with FRAME 1 is at the BOTTOM of FRAME 15.`
    : '';

  return `You are a Lead NDT Engineer performing temporal sequence analysis of UAV inspection footage.

You receive 3 sequential frames captured at fixed intervals during a drone flight:
- FRAME 1  (position ~t-1 seconds): PREVIOUS CONTEXT ONLY — do NOT annotate or box defects here
- FRAME 15 (position ~t seconds):   PRIMARY AUDIT TARGET — ALL bounding boxes must be anchored to THIS image
- FRAME 30 (position ~t+1 seconds): NEXT CONTEXT ONLY — do NOT annotate or box defects here

THE WIGGLE TEST (core methodology):
Compare the 3 frames to simulate "wiggling" the surface under inspection lighting.
- REAL DEFECTS (cracks, dents, pitting) remain FIXED relative to the metal surface across all 3 frames.
- REFLECTIONS & GLARE shift position or intensity relative to the surface as the camera moves.
- Use this test to eliminate false positives before finalizing any bounding box.

CRITICAL FRAME RULES:
- ALL [ymin, xmin, ymax, xmax] bounding box coordinates MUST be pixel-accurate relative to FRAME 15 ONLY.
- Do NOT generate coordinates referencing FRAME 1 or FRAME 30.
- Coordinate scale: 0–1000 normalized (0=top-left, 1000=bottom-right of FRAME 15).

${overlapRule}

DEFECT DETECTION RULES:
- REAL CRACKS: Deep, DARK FRACTURES. Ultra-thin (often only a few pixels wide), absorb light, remain consistently dark across all frames. NO GHOST CRACKS — only box if a clear high-contrast jagged dark line representing a physical break is visible.
- MSD (Mechanical Scratching Deformation): Numerous thin dark irregularly-oriented lines from mechanical impact. Different from singular crack fractures.
- DENTS: Localized shadow bowls — concave/convex lighting shifts across the 3 frames. Mandatory detection, precursors to cracks.
- FOULING / MILK STONE: Matte, opaque, white/yellowish deposits. If any feature has a reflective glint across frames, it is NOT fouling — it is a polish issue.
- REPAIR SITES: Localized grinding or weld-repair marks anchored to the surface. Box as 'Repair Site', recommend UF/UT check.
- UNKNOWN ANOMALIES: Any major surface disruption not fitting standard categories → box as 'Unknown Anomaly - Close-up Roping Check needed'.

${buildModeRules(modes)}

NDT DEFECT TAXONOMY (use these exact IDs for the "type" field):
    ${DEFECT_TAXONOMY}

CURRENT GROUND TRUTH LESSONS:
${groundTruth}

SENSITIVITY LEVEL: ${sensitivity}/10.

CHAIN-OF-THOUGHT REQUIRED: For each finding, the "spatialAnchoring" field must describe:
1. The exact visual appearance of the defect in FRAME 15
2. How it behaves across FRAME 1 and FRAME 30 (confirming it is NOT a reflection)
3. Why you are confident this is a real structural defect

${JSON_OUTPUT_SCHEMA}`;
};

/**
 * System prompt for the lightweight Pass 1 temporal screen.
 * Uses 640px frames and returns a simple list of confirmed defect types — no bboxes.
 */
const buildTemporalScreenPrompt = (modes: InspectionMode[]): string => {
  const targets = modes.map(m => {
    switch (m) {
      case InspectionMode.CRACK_ONLY: return 'Cracks, fissures, linear discontinuities';
      case InspectionMode.WELD_INTEGRITY: return 'Weld defects, HAZ anomalies, porosity';
      case InspectionMode.GENERAL_NDT: return 'Dents, pitting, corrosion, deformation';
      case InspectionMode.FOULING_INSPECTION: return 'Residue, scale, biological buildup';
      default: return 'General surface anomalies';
    }
  }).join(', ');

  return `You are a TEMPORAL CONSISTENCY FILTER for a UAV NDT inspection system.

You receive 3 sequential frames (FRAME 1, FRAME 15, FRAME 30) from a moving inspection camera.
Your ONLY task: identify which features visible in FRAME 15 (the middle frame) are REAL STRUCTURAL DEFECTS vs TRANSIENT REFLECTIONS or LIGHTING ARTIFACTS.

DETECTION TARGETS: ${targets}

THE REFLECTION TEST:
- Simulate "wiggling" the surface by comparing how features behave across all 3 frames.
- REAL DEFECTS: Fixed to the metal surface, maintain consistent shape/position/darkness across all 3 frames.
- REFLECTIONS / ARTIFACTS: Shift position, change intensity, or appear in only 1 of the 3 frames.

RULES:
- REAL CRACKS: Deep DARK FRACTURES. Ultra-thin, absorb light, stay consistently dark. If a feature is only a faint gradient or surface shadow — EXCLUDE it.
- MILK STONE / FOULING: Matte and opaque, stays opaque even as light angle changes. If it reflects at all — it is NOT milk stone.
- SCRATCHES: Bright reflective lines. If it has any metallic shine — it is a scratch or polish mark, NOT a crack.
- DENTS: Shadow bowls that shift subtly as the UAV moves (concave/convex light distortion).
- REPAIR SITES: Localized grinding marks, anchored to the surface.
- CAMERA MOTION HOTSPOTS: Bright spots that move relative to the surface — always reflections, always ignore.

INSTRUCTIONS:
1. Compare FRAME 1, FRAME 15, and FRAME 30 carefully.
2. Return ONLY defects you are CONFIDENT are real structural features visible in FRAME 15.
3. If a feature only appears in 1 of the 3 frames — EXCLUDE it.
4. Be conservative: when in doubt, exclude it (Pass 2 will make the final decision).

Return ONLY valid JSON (no markdown, no preamble):
{
  "confirmedDefects": [
    {
      "type": string,       // Defect type name
      "description": string // Brief visual description of the defect's appearance in FRAME 15
    }
  ]
}
Return empty array if no defects are confirmed.`;
};

// ── Exported Functions (identical signatures to geminiService.ts) ──────────────

/**
 * Analyze a single static image for surface defects.
 * Used by ImageInspector and as Pass 2 in the two-pass pipeline.
 */
export const analyzeStaticSurface = async (
  base64Image: string,
  modes: InspectionMode[] = [InspectionMode.GENERAL_NDT],
  cameraSpecs?: string,
  sensitivity: number = 5,
  budget: number = 5000,
  modelName: any = CLAUDE_MODEL,       // Parameter kept for API compatibility; always uses Claude
  confirmedContext: string = '',
  orientation?: string,
  resolutionMode: ImageResolutionMode = ImageResolutionMode.STANDARD
): Promise<CrackAnalysis> => {
  let lessons: TrainingExample[] = [];
  try { lessons = (await getVaultEntries()).slice(0, 5); } catch (_) {}

  const systemPrompt = buildStaticSystemPrompt(lessons, modes, sensitivity, confirmedContext, orientation);
  const maxPx = RESOLUTION_PX[resolutionMode];
  const jpegQuality = resolutionMode === ImageResolutionMode.ULTRA_4K ? 0.80 : 0.85;
  const optimizedImage = await optimizeImage(base64Image, maxPx, jpegQuality);
  console.log(`[NDT-RES] Static analysis — resolution: ${resolutionMode} (${maxPx}px)`);

  return callWithRetry(async () => {
    console.log(`[V6.0-CLAUDE-STATIC] Dispatching static audit...`);

    const messages: ClaudeMessage[] = [{
      role: 'user',
      content: [
        {
          type: 'image',
          source: { type: 'base64', media_type: 'image/jpeg', data: toRawBase64(optimizedImage) }
        },
        {
          type: 'text',
          text: `STILL IMAGE AUDIT TARGET — detect and box all structural anomalies.
Sensitivity: ${sensitivity}/10.
Return 2D bounding boxes as [ymin, xmin, ymax, xmax] (0–1000 normalized scale).
Return ONLY valid JSON matching the schema in your instructions.`
        }
      ]
    }];

    const response = await callClaude(systemPrompt, messages, 4096);

    if (response.usage) {
      logAiUsage(CLAUDE_MODEL, response.usage.input_tokens, response.usage.output_tokens);
    }

    const result = parseClaudeJson<CrackAnalysis>(response);
    console.log(`[V6.0-CLAUDE-STATIC] Audit complete. Findings: ${result.findings?.length || 0}. Confidence: ${result.confidenceScore}`);
    return result;
  });
};

/**
 * Analyze a 3-frame sequence (FRAME 1 → FRAME 15 → FRAME 30) for temporal defect detection.
 * All bounding boxes are anchored to the middle frame (FRAME 15, the audit target).
 */
export const analyzeSteelSequence = async (
  base64Images: string[],   // [frame1, frame15, frame30]
  modes: InspectionMode[] = [InspectionMode.GENERAL_NDT],
  cameraSpecs?: string,
  sensitivity: number = 5,
  budget: number = 0,
  modelName: any = CLAUDE_MODEL,
  movement?: 'UPWARD' | 'DOWNWARD' | 'HORIZONTAL' | 'STATIONARY',
  resolutionMode: ImageResolutionMode = ImageResolutionMode.STANDARD
): Promise<CrackAnalysis> => {
  let lessons: TrainingExample[] = [];
  try { lessons = (await getVaultEntries()).slice(0, 5); } catch (_) {}

  const systemPrompt = buildSequenceSystemPrompt(lessons, modes, sensitivity, movement);
  const maxPx = RESOLUTION_PX[resolutionMode];
  const jpegQuality = resolutionMode === ImageResolutionMode.ULTRA_4K ? 0.80 : 0.85;
  const optimizedImages = await Promise.all(base64Images.map(img => optimizeImage(img, maxPx, jpegQuality)));
  console.log(`[NDT-RES] Sequence analysis — resolution: ${resolutionMode} (${maxPx}px)`);

  return callWithRetry(async () => {
    // Frame ordering in the message matches the temporal screen convention:
    // FRAME 1 first (previous context), FRAME 30 second (next context),
    // FRAME 15 LAST — placing the audit target immediately before the task prompt
    // anchors Claude's spatial attention and bounding box coordinates to FRAME 15.
    const messages: ClaudeMessage[] = [{
      role: 'user',
      content: [
        { type: 'text', text: 'FRAME 1 (PREVIOUS CONTEXT — temporal reference only, do NOT box defects here):' },
        { type: 'image', source: { type: 'base64', media_type: 'image/jpeg', data: toRawBase64(optimizedImages[0]) } },
        { type: 'text', text: 'FRAME 30 (NEXT CONTEXT — temporal reference only, do NOT box defects here):' },
        { type: 'image', source: { type: 'base64', media_type: 'image/jpeg', data: toRawBase64(optimizedImages[2]) } },
        { type: 'text', text: 'FRAME 15 — PRIMARY AUDIT TARGET (ALL bounding boxes MUST be anchored to THIS image only):' },
        { type: 'image', source: { type: 'base64', media_type: 'image/jpeg', data: toRawBase64(optimizedImages[1]) } },
        {
          type: 'text',
          text: `TASK: Detect and box structural anomalies ONLY in FRAME 15 (the last image above).
All [ymin, xmin, ymax, xmax] coordinates MUST be pixel-accurate relative to FRAME 15 ONLY.
Do NOT generate coordinates referencing FRAME 1 or FRAME 30.
Coordinate scale: 0–1000 normalized (0=top-left, 1000=bottom-right of FRAME 15).
Sensitivity: ${sensitivity}/10.
Apply the wiggle test: use FRAME 1 and FRAME 30 to confirm each defect is fixed to the surface, not a reflection.
Return ONLY valid JSON matching the schema in your instructions.`
        }
      ]
    }];

    console.log(`[V6.0-CLAUDE-SEQUENCE] Dispatching 3-frame audit (F1/F15/F30)...`);
    const response = await callClaude(systemPrompt, messages, 4096);

    if (response.usage) {
      logAiUsage(CLAUDE_MODEL, response.usage.input_tokens, response.usage.output_tokens);
    }

    const result = parseClaudeJson<CrackAnalysis>(response);
    console.log(`[V6.0-CLAUDE-SEQUENCE] Audit complete. Findings: ${result.findings?.length || 0}. Confidence: ${result.confidenceScore}`);
    return result;
  });
};

/**
 * NDT Advisor chat — answers technical questions about inspection findings.
 */
export const queryAdvisor = async (query: string): Promise<string> => {
  // Diagnostic command (kept for compatibility)
  if (query.toLowerCase() === 'listmodels') {
    return `Claude Model Active: ${CLAUDE_MODEL} | NDT Vision Service V6.0`;
  }

  try {
    const response = await callClaude(
      'You are an Expert NDT Advisor specializing in food-grade stainless steel inspection, UAV-based audits, and EN/ISO NDT standards. Provide concise, technically accurate responses.',
      [{ role: 'user', content: [{ type: 'text', text: query }] }],
      1024
    );

    if (response.usage) {
      logAiUsage(CLAUDE_MODEL, response.usage.input_tokens, response.usage.output_tokens);
    }

    return response.content.filter(b => b.type === 'text').map(b => b.text || '').join('') || 'No response received.';
  } catch (err: any) {
    return `Technical link failure: ${err.message}`;
  }
};

// ── Pass 1: Lightweight Temporal Screen ───────────────────────────────────────

/**
 * Pass 1 of the two-pass pipeline.
 * Sends 3 downscaled frames (640px) to Claude and returns a list of confirmed
 * real defects vs reflections — without bounding boxes.
 * 
 * Fast and cheap: uses smaller images to act as a reflection filter for Pass 2.
 */
const temporalScreen = async (
  framePrev: string,   // FRAME 1
  frameCurr: string,   // FRAME 15 (audit target)
  frameNext: string,   // FRAME 30
  modes: InspectionMode[]
): Promise<{ type: string; description: string }[]> => {
  const systemPrompt = buildTemporalScreenPrompt(modes);

  // Downscale to 640px — sufficient for temporal comparison, minimises latency & cost
  const [opt1, opt15, opt30] = await Promise.all([
    optimizeImage(framePrev, 640),
    optimizeImage(frameCurr, 640),
    optimizeImage(frameNext, 640),
  ]);

  try {
    const messages: ClaudeMessage[] = [{
      role: 'user',
      content: [
        { type: 'text', text: 'FRAME 1 (PREVIOUS CONTEXT):' },
        { type: 'image', source: { type: 'base64', media_type: 'image/jpeg', data: toRawBase64(opt1) } },
        { type: 'text', text: 'FRAME 15 (CURRENT — middle frame, the audit target):' },
        { type: 'image', source: { type: 'base64', media_type: 'image/jpeg', data: toRawBase64(opt15) } },
        { type: 'text', text: 'FRAME 30 (NEXT CONTEXT):' },
        { type: 'image', source: { type: 'base64', media_type: 'image/jpeg', data: toRawBase64(opt30) } },
        {
          type: 'text',
          text: 'Apply the wiggle test. Compare all 3 frames and list ONLY the structural defects in FRAME 15 that appear consistently (not reflections). Return ONLY valid JSON.'
        }
      ]
    }];

    const response = await callClaude(systemPrompt, messages, 1024);

    if (response.usage) {
      logAiUsage(CLAUDE_MODEL, response.usage.input_tokens, response.usage.output_tokens);
    }

    const result = parseClaudeJson<{ confirmedDefects: { type: string; description: string }[] }>(response);
    console.log(`[NDT-TEMPORAL-SCREEN] Confirmed real defects: ${result.confirmedDefects?.length || 0}`);
    return result.confirmedDefects || [];
  } catch (e) {
    console.warn('[NDT-TEMPORAL-SCREEN] Screen failed — proceeding without filter:', e);
    return []; // Safe fallback: empty = no filter applied, Pass 2 runs as free-scan
  }
};

// ── Two-Pass Analysis Orchestrator ────────────────────────────────────────────

/**
 * Full two-pass pipeline:
 * - Pass 1 (temporalScreen): lightweight 3-frame reflection filter at 640px
 * - Pass 2 (analyzeStaticSurface): precise single-frame bbox analysis at full resolution,
 *   guided by the confirmed-defect list from Pass 1
 * 
 * Frame convention: framePrev=FRAME 1, frameCurr=FRAME 15, frameNext=FRAME 30
 */
export const analyzeTwoPass = async (
  framePrev: string,
  frameCurr: string,
  frameNext: string,
  modes: InspectionMode[] = [InspectionMode.GENERAL_NDT],
  cameraSpecs?: string,
  sensitivity: number = 5,
  budget: number = 6000,
  modelName: any = CLAUDE_MODEL,
  orientation?: string,
  resolutionMode: ImageResolutionMode = ImageResolutionMode.STANDARD
): Promise<CrackAnalysis> => {
  // Pass 1: temporal screen (runs first to build confirmed defect list)
  const confirmedDefects = await temporalScreen(framePrev, frameCurr, frameNext, modes);

  // Build the confirmed-context string injected into Pass 2's prompt
  let confirmedContext = '';
  if (confirmedDefects.length > 0) {
    const list = confirmedDefects
      .map((d, i) => `  ${i + 1}. TYPE: "${d.type}" — ${d.description}`)
      .join('\n');
    confirmedContext = `TEMPORAL VERIFICATION COMPLETE — ${confirmedDefects.length} CONFIRMED STRUCTURAL ANOMALY/ANOMALIES:
${list}

STRICT RULE: Only box defects from the list above. These have been verified as real structural
features across FRAME 1, FRAME 15, and FRAME 30. Do NOT box reflections, glare, or unverified features.
If a confirmed defect is not clearly visible in FRAME 15, skip it.`.trim();
  }
  // If Pass 1 returned nothing (failed or found nothing), Pass 2 runs as full free-scan.

  // Pass 2: precise single-frame analysis with confirmed-defect context
  return analyzeStaticSurface(
    frameCurr,
    modes,
    cameraSpecs,
    sensitivity,
    budget,
    modelName,
    confirmedContext,
    orientation,
    resolutionMode
  );
};
